/**
 * Capacitor App Initialization
 * Handles native platform setup for iOS/Android
 */
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Keyboard } from '@capacitor/keyboard';
import { Browser } from '@capacitor/browser';
import { hasSessionToken, refreshAuth } from './api';
import { requestLeave } from './unsavedChangesGuard';

/**
 * Open a URL without leaving the app (Apple Guideline 4 compliance).
 *
 * On native (iOS/Android) we use @capacitor/browser, which presents an
 * in-app SFSafariViewController (iOS) / Chrome Custom Tab (Android) instead
 * of bouncing the user out to the default external browser. On the web we
 * fall back to a normal new tab.
 *
 * Use this for every user-facing external link rendered inside the native
 * shell (legal pages, manage-subscription deep links, attribution links,
 * OAuth flows, etc.).
 */
export async function openInAppBrowser(url) {
  if (Capacitor.isNativePlatform()) {
    try {
      await Browser.open({ url });
      return;
    } catch (e) {
      console.warn('[Capacitor] Browser.open failed, falling back to window.open:', e?.message);
    }
  }
  window.open(url, '_blank', 'noopener,noreferrer');
}

// Minimum inactive period before we re-validate the session on resume.
// Short enough to catch stale tokens promptly when the user returns to
// the app, long enough not to ping /auth/me on every brief context switch
// (e.g. tapping out to copy a verification code).
const SESSION_REVALIDATE_AFTER_MS = 10 * 60 * 1000; // 10 minutes

// Wall-clock timestamp the app was last observed active (or initialised).
// Updated on every appStateChange so the next resume can compute the gap.
let _lastActiveAt = Date.now();

/**
 * Initialize native platform features
 * Called once when the app starts
 */
export async function initializeCapacitor() {
  // Only run on native platforms
  if (!Capacitor.isNativePlatform()) {
    console.log('[Capacitor] Running on web, skipping native initialization');
    return;
  }

  console.log('[Capacitor] Initializing native platform:', Capacitor.getPlatform());

  try {
    // Configure Status Bar.
    // NOTE: Capacitor's @capacitor/status-bar naming is counter-intuitive —
    // Style.Light means DARK text/icons (use on LIGHT app backgrounds) and
    // Style.Dark means WHITE text/icons (use on DARK app backgrounds).
    // The Fitxess app defaults to the LIGHT theme (white background), so we
    // use Style.Light → dark status-bar icons that are visible on the white
    // screen content. Previously the app used Style.Dark which produced
    // white-on-white (invisible) icons on the Grocery locked screen and
    // muted-contrast icons elsewhere.
    await StatusBar.setStyle({ style: Style.Light });
    
    if (Capacitor.getPlatform() === 'android') {
      // Match the Android status-bar bg to the app's light background so the
      // dark icons above also land on a light surface (Android shows a solid
      // status-bar bg; iOS draws the bar transparently over page content).
      await StatusBar.setBackgroundColor({ color: '#FFFFFF' });
    }
    
    console.log('[Capacitor] Status bar configured (Style.Light → dark icons for light app bg)');
  } catch (error) {
    console.warn('[Capacitor] Status bar configuration failed:', error);
  }

  try {
    // Configure Keyboard behavior (iOS)
    if (Capacitor.getPlatform() === 'ios') {
      Keyboard.setAccessoryBarVisible({ isVisible: true });
      Keyboard.setScroll({ isDisabled: false });
    }
    console.log('[Capacitor] Keyboard configured');
  } catch (error) {
    console.warn('[Capacitor] Keyboard configuration failed:', error);
  }

  // Handle app URL open (for deep links)
  App.addListener('appUrlOpen', (event) => {
    console.log('[Capacitor][AUTH] appUrlOpen FIRED with URL:', event.url);

    // The Google OAuth flow is now presented in an in-app SFSafariViewController
    // (via openInAppBrowser → Browser.open). When the backend redirects to our
    // custom-scheme deep link the OS hands control back to the app, but the
    // in-app browser sheet stays on screen — dismiss it so the user lands
    // directly on the authenticated view.
    Browser.close().catch(() => { /* no browser open — safe to ignore */ });
    
    // Parse the deep link URL. Expected shapes:
    //   fitxess://auth/magic-link?token=xxx      (magic link)
    //   fitxess://auth-callback?session_id=xxx   (Google OAuth callback)
    try {
      const url = new URL(event.url);
      // For custom-scheme URLs, `host` holds the first path segment (e.g.
      // `auth-callback` in `fitxess://auth-callback?...`). Combine host + pathname
      // so we can match both forms reliably.
      const path = `${url.host || ''}${url.pathname || ''}`;
      console.log('[Capacitor][AUTH] parsed path=', path, 'search=', url.search);

      // --- Google OAuth callback ---
      if (path.includes('auth-callback')) {
        const sessionId = url.searchParams.get('session_id');
        console.log('[Capacitor][AUTH] Google callback branch. session_id present?', !!sessionId);
        if (sessionId) {
          window.location.href = '/auth-callback?session_id=' + encodeURIComponent(sessionId);
          return;
        }
      }

      // --- Magic link ---
      if (path.includes('auth/magic-link') || path.includes('magic-link')) {
        const token = url.searchParams.get('token');
        console.log('[Capacitor][AUTH] Magic link branch. token present?', !!token);
        if (token) {
          window.localStorage.setItem('pending_magic_token', token);
          window.location.href = '/auth-callback?magic_token=' + encodeURIComponent(token);
        }
      }
    } catch (error) {
      console.error('[Capacitor][AUTH] Error parsing deep link:', error);
    }
  });

  // Handle app state changes
  App.addListener('appStateChange', async (state) => {
    console.log('[Capacitor] App state changed:', state.isActive ? 'active' : 'background');
    if (!state.isActive) {
      // Returning to background — capture the timestamp so the next
      // resume can compute the inactive gap.
      _lastActiveAt = Date.now();
      return;
    }
    // Resumed to foreground. Decide whether to re-validate the session.
    const inactiveMs = Date.now() - _lastActiveAt;
    _lastActiveAt = Date.now();
    if (inactiveMs < SESSION_REVALIDATE_AFTER_MS) return;
    // Only validate when a token actually exists — never bounce a
    // signed-out user (who is already on /login or onboarding).
    if (!hasSessionToken()) return;
    console.log(
      '[Capacitor][AUTH] Resumed after',
      Math.round(inactiveMs / 60000),
      'min idle — revalidating session via /auth/me'
    );
    try {
      await refreshAuth();
      console.log('[Capacitor][AUTH] Session still valid on resume');
    } catch (err) {
      // 401 path: the axios interceptor has already cleared storage and
      // navigated to /login?error=session_expired before this catch
      // runs. Any other failure (network outage, 5xx) is intentionally
      // silent — the very next user action will retry naturally.
      console.warn('[Capacitor][AUTH] Resume revalidation failed:', err?.message || err);
    }
  });

  // Handle back button (Android)
  App.addListener('backButton', ({ canGoBack }) => {
    // A screen with unsaved edits (e.g. Settings food preferences) may take
    // over and show its own Save / Discard dialog instead.
    if (!requestLeave(null)) return;
    if (!canGoBack) {
      // Could show exit confirmation here
      // App.exitApp();
    } else {
      window.history.back();
    }
  });

  console.log('[Capacitor] Native initialization complete');
}

/**
 * Check if running on native platform
 */
export function isNativePlatform() {
  return Capacitor.isNativePlatform();
}

/**
 * Get current platform
 */
export function getPlatform() {
  return Capacitor.getPlatform();
}

export default {
  initializeCapacitor,
  isNativePlatform,
  getPlatform,
};
